/* Iter123k247 · Wizard "Benvenuto in Ginius" per il primo login del trial.
   3 step bloccanti: Dati Studio → Logo/Firma → Primo condominio di prova.
   Si monta a livello App (vedi App.js) e intercetta QUALSIASI pagina finché
   `applicable && !completed`. Il backend `/api/me/onboarding/status` è la fonte
   di verità: lo stato dei 3 step viene ricalcolato ad ogni avanzamento. */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import LogoFirmaCard from "./LogoFirmaCard";
import {
  Sparkles, Building2, Palette, Home, CheckCircle2, Loader2, ArrowRight,
  AlertTriangle, LogOut,
} from "lucide-react";

const CAMPI_TITOLARE = [
  { k: "ragione_sociale", label: "Ragione sociale", placeholder: "Es. Studio Rossi di Mario Rossi", required: true, autoFocus: true },
  { k: "indirizzo", label: "Indirizzo (via, civico)", placeholder: "Es. Via Roma 1", required: true },
  { k: "cap", label: "CAP", placeholder: "Es. 20025", required: true, size: "short" },
  { k: "citta", label: "Città", placeholder: "Es. Legnano", required: true },
  { k: "provincia", label: "Provincia", placeholder: "Es. MI", required: true, size: "short" },
  { k: "email", label: "Email di contatto", placeholder: "Es. info@studiorossi.it", required: true, type: "email" },
  { k: "pec", label: "PEC", placeholder: "Es. studiorossi@pec.it", required: true, type: "email" },
  { k: "telefono", label: "Telefono", placeholder: "Es. 0331 123456" },
  { k: "codice_fiscale", label: "Codice Fiscale", placeholder: "Es. RSSMRA80A01F205X", required: true },
  { k: "partita_iva", label: "Partita IVA", placeholder: "Es. 01234567890", required: true },
  { k: "legale_rappresentante", label: "Legale rappresentante", placeholder: "Es. Mario Rossi" },
];

export default function OnboardingWizard({ onComplete }) {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  // Step 1 form
  const [tit, setTit] = useState({});
  // Step 3 form (prefill sample data)
  const [cond, setCond] = useState({
    nome: "Condominio Via Garibaldi 10",
    indirizzo: "Via Garibaldi 10",
    cap: "20025",
    citta: "Legnano",
    provincia: "MI",
    condomino_nome: "Mario Rossi",
    condomino_email: "",
  });
  const mounted = useRef(true);

  // Carica stato onboarding + prefill titolare
  const refresh = async () => {
    const { data } = await api.get("/me/onboarding/status");
    if (!mounted.current) return;
    setStatus(data);
    // Determina lo step corrente: il primo non ancora completato
    if (!data.step_dati_studio) setStep(1);
    else if (!data.step_branding) setStep(2);
    else if (!data.step_condominio) setStep(3);
    else setStep(4); // completato
    return data;
  };

  useEffect(() => {
    mounted.current = true;
    // Prefill step 1 con quello che c'è già in /me/tenant/titolare
    api.get("/me/tenant/titolare").then(({ data }) => {
      if (!mounted.current) return;
      const prefill = {};
      for (const { k } of CAMPI_TITOLARE) prefill[k] = data?.[k] || "";
      setTit(prefill);
    }).catch(() => {});
    refresh();
    return () => { mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter123k247 · appena diventa `completed`, ricarica l'app così la Dashboard,
  // la trial-bar e i banner titolare/branding mostrano lo stato aggiornato.
  // Side-effect in useEffect (non in render) come indicato dal code review.
  // Reload SOLO sulla transizione incompleto→completo avvenuta in questa sessione
  // (altrimenti un trial già completato entra in un loop infinito di reload).
  const wasIncomplete = useRef(false);
  useEffect(() => {
    if (!status) return;
    if (status.applicable && !status.completed) { wasIncomplete.current = true; return; }
    if (!status.applicable || !wasIncomplete.current) return;
    wasIncomplete.current = false;
    if (typeof onComplete === "function") onComplete();
    // Hard reload per rinfrescare ogni consumer di `/me/*` in sidebar/dashboard.
    window.location.reload();
  }, [status, onComplete]);

  if (!status) {
    // Loading iniziale: mantengo l'overlay scuro per evitare il flash della UI sottostante.
    return (
      <div data-testid="onboarding-wizard-loading"
        className="fixed inset-0 z-[60] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-white animate-spin" />
      </div>
    );
  }
  if (!status.applicable || status.completed) {
    // Il reload sopra sta per avvenire → non mostriamo nulla.
    return null;
  }

  // --- Step 1 · Dati Studio ---
  const saveStep1 = async () => {
    // Valida: tutti i required valorizzati
    const missing = CAMPI_TITOLARE
      .filter((c) => c.required && !(tit[c.k] || "").trim())
      .map((c) => c.label);
    if (missing.length) {
      toast.error(`Compila: ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      await api.put("/me/tenant/titolare", tit);
      toast.success("Dati Studio salvati");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  // --- Step 2 · Branding (upload gestito da LogoFirmaCard; l'utente conferma col bottone) ---
  // Lo step NON avanza automaticamente: il click su "Ho caricato tutto, prosegui"
  // chiama /me/onboarding/status così il backend verifica che ENTRAMBI logo e firma
  // siano caricati prima di sbloccare lo step 3.
  const checkStep2 = async () => {
    const data = await refresh();
    if (data?.step_branding) {
      toast.success("Branding caricato");
    } else {
      toast.error("Carica sia logo che firma per proseguire");
    }
  };

  // --- Step 3 · Primo condominio di prova ---
  const saveStep3 = async () => {
    if (!(cond.nome || "").trim() || !(cond.indirizzo || "").trim()) {
      toast.error("Nome e indirizzo del condominio sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/me/onboarding/condominio-demo", cond);
      toast.success(data.already_existed
        ? "Hai già un condominio: proseguiamo!"
        : `Condominio «${data.nome}» creato con 2 unità e un condomino di esempio`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Creazione non riuscita");
    } finally { setSaving(false); }
  };

  const totalSteps = 3;
  const progress = Math.min(step - 1, totalSteps);

  return (
    <div data-testid="onboarding-wizard-overlay"
      className="fixed inset-0 z-[60] bg-slate-900/85 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
        <Card className="w-full max-w-3xl p-5 sm:p-7 shadow-2xl" data-testid="onboarding-wizard-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 rounded-2xl p-3 bg-brand text-white">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-brand font-bold text-xl sm:text-2xl text-slate-900 leading-tight">
                Benvenuto in Ginius, {status.user_name?.split(" ")[0] || "collega"}!
              </h1>
              <p className="text-sm text-slate-600 leading-snug mt-0.5">
                Attiviamo il tuo Studio in <b>3 passi veloci</b>: tutti e tre sono richiesti per iniziare a usare l&apos;app.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}
              data-testid="onboarding-wizard-logout"
              className="shrink-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100">
              <LogOut className="w-4 h-4 mr-1" /> Esci
            </Button>
          </div>

          <StepperBar step={step} />

          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2 text-xs text-amber-900"
               data-testid="onboarding-bloccante-note">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              Hai attivato una prova gratuita: completa i 3 step per sbloccare preventivi, sinistri e CoPilot.
              I dati inseriti qui sono reali e resteranno con te quando passerai al piano a pagamento.
            </div>
          </div>

          {step === 1 && (
            <Step1DatiStudio tit={tit} setTit={setTit} saving={saving} onSave={saveStep1} />
          )}
          {step === 2 && (
            <Step2Branding saving={saving} onCheck={checkStep2} />
          )}
          {step === 3 && (
            <Step3Condominio cond={cond} setCond={setCond} saving={saving} onSave={saveStep3} />
          )}

          <div className="mt-5 text-[11px] text-slate-500 text-center">
            Progresso: {progress} di {totalSteps} completati
          </div>
        </Card>
      </div>
    </div>
  );
}

function StepperBar({ step }) {
  const stages = [
    { n: 1, icon: Building2, label: "Dati Studio" },
    { n: 2, icon: Palette, label: "Logo & Firma" },
    { n: 3, icon: Home, label: "Primo Condominio" },
  ];
  return (
    <ol className="mb-5 grid grid-cols-3 gap-2" data-testid="onboarding-stepper">
      {stages.map(({ n, icon: Icon, label }) => {
        const done = step > n;
        const active = step === n;
        return (
          <li key={n} data-testid={`onboarding-step-${n}`}
            className={`rounded-lg p-2 flex items-center gap-2 border-2 ${
              done ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                   : active ? "bg-brand/10 border-brand text-brand-dark"
                            : "bg-slate-50 border-slate-200 text-slate-500"
            }`}>
            {done
              ? <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
              : <Icon className="w-5 h-5 shrink-0" />}
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase opacity-70">Step {n}</div>
              {/* Nasconde il label su mobile per non troncarlo; resta l'icona + numero. */}
              <div className="hidden sm:block text-sm font-bold leading-tight truncate">{label}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Step1DatiStudio({ tit, setTit, saving, onSave }) {
  return (
    <div data-testid="onboarding-step1">
      <h2 className="font-bold text-base text-slate-900 mb-1 flex items-center gap-2">
        <Building2 className="w-5 h-5 text-brand" /> Dati del tuo Studio
      </h2>
      <p className="text-xs text-slate-600 mb-4 leading-snug">
        Servono per emettere documenti e preventivi a norma GDPR (sei il «Titolare del trattamento» dei tuoi condomìni).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        {CAMPI_TITOLARE.map((c) => (
          <div key={c.k} className={c.size === "short" ? "sm:col-span-2" : "sm:col-span-3"}>
            <Label htmlFor={`ob-${c.k}`} className="text-xs text-slate-700">
              {c.label} {c.required && <span className="text-brand">*</span>}
            </Label>
            <Input id={`ob-${c.k}`} type={c.type || "text"}
              value={tit[c.k] || ""}
              onChange={(e) => setTit({ ...tit, [c.k]: e.target.value })}
              placeholder={c.placeholder || ""}
              autoFocus={c.autoFocus}
              data-testid={`ob-tit-${c.k}`} className="h-10" />
          </div>
        ))}
      </div>
      <div className="mt-5 flex justify-end">
        <Button onClick={onSave} disabled={saving}
          data-testid="ob-step1-save"
          className="bg-brand hover:bg-brand-dark text-white h-11 px-6">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <ArrowRight className="w-4 h-4 mr-1" />}
          Salva e prosegui
        </Button>
      </div>
    </div>
  );
}

function Step2Branding({ saving, onCheck }) {
  return (
    <div data-testid="onboarding-step2">
      <h2 className="font-bold text-base text-slate-900 mb-1 flex items-center gap-2">
        <Palette className="w-5 h-5 text-brand" /> Logo e firma del tuo Studio
      </h2>
      <p className="text-xs text-slate-600 mb-4 leading-snug">
        Carica <b>entrambi</b> trascinando i file: appariranno su tutti i PDF (preventivi, sinistri, Credius).
      </p>
      <LogoFirmaCard />
      <div className="mt-4 flex justify-end">
        <Button onClick={onCheck} disabled={saving}
          data-testid="ob-step2-next"
          className="bg-brand hover:bg-brand-dark text-white h-11 px-6">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <ArrowRight className="w-4 h-4 mr-1" />}
          Ho caricato tutto, prosegui
        </Button>
      </div>
    </div>
  );
}

function Step3Condominio({ cond, setCond, saving, onSave }) {
  return (
    <div data-testid="onboarding-step3">
      <h2 className="font-bold text-base text-slate-900 mb-1 flex items-center gap-2">
        <Home className="w-5 h-5 text-brand" /> Il tuo primo condominio di prova
      </h2>
      <p className="text-xs text-slate-600 mb-4 leading-snug">
        Lo creiamo con <b>2 unità (interno 1 e 2)</b> e un <b>condomino di esempio</b>, così vedi subito l&apos;app popolata.
        Potrai modificarlo o eliminarlo in qualsiasi momento.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        <div className="sm:col-span-3">
          <Label htmlFor="ob-cond-nome" className="text-xs text-slate-700">Nome condominio *</Label>
          <Input id="ob-cond-nome" value={cond.nome}
            onChange={(e) => setCond({ ...cond, nome: e.target.value })}
            data-testid="ob-cond-nome" className="h-10" />
        </div>
        <div className="sm:col-span-3">
          <Label htmlFor="ob-cond-indirizzo" className="text-xs text-slate-700">Indirizzo *</Label>
          <Input id="ob-cond-indirizzo" value={cond.indirizzo}
            onChange={(e) => setCond({ ...cond, indirizzo: e.target.value })}
            data-testid="ob-cond-indirizzo" className="h-10" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="ob-cond-cap" className="text-xs text-slate-700">CAP</Label>
          <Input id="ob-cond-cap" value={cond.cap}
            onChange={(e) => setCond({ ...cond, cap: e.target.value })}
            data-testid="ob-cond-cap" className="h-10" />
        </div>
        <div className="sm:col-span-3">
          <Label htmlFor="ob-cond-citta" className="text-xs text-slate-700">Città</Label>
          <Input id="ob-cond-citta" value={cond.citta}
            onChange={(e) => setCond({ ...cond, citta: e.target.value })}
            data-testid="ob-cond-citta" className="h-10" />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="ob-cond-provincia" className="text-xs text-slate-700">Prov</Label>
          <Input id="ob-cond-provincia" value={cond.provincia}
            onChange={(e) => setCond({ ...cond, provincia: e.target.value })}
            data-testid="ob-cond-provincia" className="h-10" />
        </div>
        <div className="sm:col-span-3">
          <Label htmlFor="ob-cond-cnome" className="text-xs text-slate-700">Nome condomino di esempio</Label>
          <Input id="ob-cond-cnome" value={cond.condomino_nome}
            onChange={(e) => setCond({ ...cond, condomino_nome: e.target.value })}
            data-testid="ob-cond-condomino-nome" className="h-10" />
        </div>
        <div className="sm:col-span-3">
          <Label htmlFor="ob-cond-cemail" className="text-xs text-slate-700">Email condomino (opzionale)</Label>
          <Input id="ob-cond-cemail" type="email" value={cond.condomino_email}
            onChange={(e) => setCond({ ...cond, condomino_email: e.target.value })}
            placeholder="mario.rossi@email.it"
            data-testid="ob-cond-condomino-email" className="h-10" />
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button onClick={onSave} disabled={saving}
          data-testid="ob-step3-save"
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <CheckCircle2 className="w-4 h-4 mr-1" />}
          Crea e completa l&apos;attivazione
        </Button>
      </div>
    </div>
  );
}
